import {
  BooleanInput,
  CheckboxGroupInput,
  email,
  FileField,
  FileInput,
  FormTab,
  maxLength,
  NumberInput,
  required,
  SelectInput,
  TabbedForm,
  TextInput,
  usePermissions,
} from 'react-admin'
import { Field } from 'react-final-form'

import { useHasBrokerCarrierRelationship } from 'hooks/useHasFeatureFlag'
import {
  BOOKING_TYPE_CHOICES,
  BROKER_PAYMENT_METHOD_CHOICES,
  BROKER_SUBMISSION_METHOD_CHOICES,
  BrokerSubmissionMethods,
} from '../../helpers/constants'
import {
  mobileValidator,
  supportPhoneValidator,
  validateFEINNumber,
  validateMCNumber,
} from '../../helpers/validators'
import { hasPermission, Permissions } from '../../Permissions'
import { getBrokerTransitionProps } from '../Brokers/BrokerStatusTransitions'
import { AddressInputForm } from '../Common/AddressForm'
import {
  validateBolContactEmail,
  validateCarrierAgreement,
  validateCarrierPortalLink,
} from '../Common/Brokers'
import { ConditionalInput } from '../Fields'

const emailValidator = email()

export const BrokerForm = (props: any) => {
  const { permissions } = usePermissions()
  const hasBrokerCarrierRelationship = useHasBrokerCarrierRelationship()
  const canToggleAutoInvoicing =
    permissions && hasPermission(permissions, Permissions.ChangeAutoInvoiceBroker)
  return (
    <TabbedForm {...props} toolbar={props.toolbar} submitOnEnter={false}>
      <FormTab label="Overview">
        <TextInput source="name" validate={[required()]} />
        <TextInput source="dot_number" validate={maxLength(8)} />
        <BooleanInput source="is_shipper" validate={required()} initialValue={false} />
        <ConditionalInput when="is_shipper" is={true}>
          <TextInput source="fein_number" label="FEIN number" validate={validateFEINNumber} fullWidth />
        </ConditionalInput>
        <ConditionalInput when="is_shipper" is={false}>
          <TextInput source="mc_number" label="MC number" validate={validateMCNumber} fullWidth />
        </ConditionalInput>
        {!hasBrokerCarrierRelationship && (
          <CheckboxGroupInput source="booking_types" choices={BOOKING_TYPE_CHOICES} />
        )}
        <Field name="bol_submission_method" subscription={{ value: true }}>
          {({ input }) => {
            return (
              <TextInput
                source="carrier_portal_link"
                label="Invoicing Portal Link"
                validate={validateCarrierPortalLink}
                isRequired={input.value === BrokerSubmissionMethods.CarrierPortal}
              />
            )
          }}
        </Field>
        <BooleanInput source="trucklists_enabled" initialValue={props.record.trucklists_enabled} />
        {!hasBrokerCarrierRelationship && (
          <>
            <Field
              name="is_carrier_packet_complete"
              subscription={{ value: true }}
              label="Old MC Carrier packet complete?"
            >
              {({ input }) => {
                return (
                  <FileInput
                    source="carrier_agreement"
                    validate={validateCarrierAgreement}
                    isRequired={input.value}
                    label="Old MC Carrier agreement"
                  >
                    <FileField title="Carrier agreement" />
                  </FileInput>
                )
              }}
            </Field>
            <BooleanInput
              source="is_carrier_packet_complete"
              initialValue={props.record.is_carrier_packet_complete}
              validate={required()}
            />
            <SelectInput
              source="status"
              choices={getBrokerTransitionProps(props.record.status)}
              validate={required()}
              initialValue={props.record.status}
              label="Old MC Status"
            />
            <br />
            <SelectInput
              label="CT MC #1532905 Status"
              source="mc_23_status"
              choices={getBrokerTransitionProps(props.record.mc_23_status)}
              validate={required()}
              initialValue={props.record.mc_23_status}
            />
          </>
        )}
      </FormTab>
      <FormTab label="Billing address">
        <AddressInputForm sourceParent="billing_address" />
      </FormTab>
      <FormTab label="Contact info">
        {!hasBrokerCarrierRelationship && (
          <>
            <TextInput source="support_main_phone" validate={mobileValidator} />
            <br />
            <TextInput source="support_main_email" validate={emailValidator} />
            <BooleanInput
              source="use_support_email_for_tracking"
              label="Use to send job tracking information"
            />
          </>
        )}
        <Field name="bol_submission_method" subscription={{ value: true }}>
          {({ input }) => {
            return (
              <TextInput
                source="bol_contact_email"
                label="VC Invoicing Email"
                validate={validateBolContactEmail}
                isRequired={input.value === BrokerSubmissionMethods.CarrierPortal}
              />
            )
          }}
        </Field>
        <TextInput
          source="factoring_invoicing_email"
          label="Factoring Invoicing Email"
          validate={emailValidator}
        />
        <TextInput source="factoring_noa_email" label="Factoring NOA Email" validate={emailValidator} />
        <TextInput
          source="pay_status_inquiry_email"
          label="Pay Status Inquiry Email"
          validate={emailValidator}
        />
        <TextInput source="support_after_hours_phone" validate={mobileValidator} />
        <TextInput source="support_after_hours_email" validate={emailValidator} />
        {!hasBrokerCarrierRelationship && (
          <>
            <TextInput source="booking_contact_phone" validate={supportPhoneValidator} />
            <br />
            <TextInput source="booking_contact_email" validate={emailValidator} />
          </>
        )}
        <TextInput source="trucklists_email" validate={emailValidator} />
      </FormTab>
      <FormTab label="Invoicing">
        <SelectInput
          source="bol_submission_method"
          choices={BROKER_SUBMISSION_METHOD_CHOICES}
          label="Invoice Submission Method"
        />
        {!hasBrokerCarrierRelationship && (
          <ConditionalInput when="bol_submission_method" is={BrokerSubmissionMethods.Email}>
            <BooleanInput
              source="is_invoicing_submission_automated"
              label="Automated Invoicing Submission"
              initialValue={false}
              disabled={!canToggleAutoInvoicing}
            />
          </ConditionalInput>
        )}
        <SelectInput
          source="tracking_submission_method"
          choices={BROKER_SUBMISSION_METHOD_CHOICES}
          label="Job Tracking Submission Method"
        />

        <NumberInput source="payment_terms_days" validate={required()} />
        {!hasBrokerCarrierRelationship && (
          <SelectInput source="payment_method" choices={BROKER_PAYMENT_METHOD_CHOICES} />
        )}
        <NumberInput source="layover_rate_per_day" />
        <NumberInput source="detention_rate_per_hour" />
        <NumberInput source="detention_start_time_hours" />
        <NumberInput source="detention_max_hours_per_day" />
        <NumberInput source="tonu_rate" />
        {!hasBrokerCarrierRelationship && (
          <TextInput
            source="submission_requirement_notes"
            label="Invoicing Submission Requirements"
            fullWidth
            multiline
            component="pre"
          />
        )}
      </FormTab>
      <FormTab label="Notes">
        <TextInput source="notes" fullWidth multiline component="pre" />
      </FormTab>
    </TabbedForm>
  )
}

export const successCallback = ({ data, notify, redirectTo, basePath }: any) => {
  notify('Success!', { type: 'success' })
  redirectTo('show', basePath, data.id, data)
}

export const failureCallback = ({ error, notify }: any) => {
  notify(`Oops, something went wrong: ${JSON.stringify(error.body)}`, { type: 'error' })
}
